import React, { useState } from 'react';
import {
  Row, Col, Spinner, Form,
} from 'react-bootstrap';

import TooltipWrapper from 'components/DesignSystem/TooltipWrapper';
import Switch from 'components/DesignSystem/Switch';
import useProductsOptions from 'utils/hooks/useProductsOptions';
import useIsMobile from 'utils/hooks/useIsMobile';
import useDateRange from 'utils/hooks/useDateRange';
import ActionButton from 'components/ActionButton';
import { ContractProduct } from 'helper/contracts';
import { useFromUnixTime } from 'utils/hooks';
import DatePicker from 'components/DatePicker';
import Selector from 'components/DesignSystem/Selector';

interface ProductProps {
  index: number,
  removeProductLineMethod: (elementIndex: number) => void,
  defaultStartDate: Date | null,
  defaultEndDate: Date | null,
  contractProduct?: ContractProduct,
}

export default function Product({
  index,
  removeProductLineMethod,
  defaultStartDate,
  defaultEndDate,
  contractProduct,
}: ProductProps) {
  const [selectedProduct, setSelectedProduct] = useState(contractProduct?.productId);
  const productKey = `product${index}`;
  const fieldId = `product-id-${index}`;
  const {
    startDate, endDate, onChangeStartDate, onChangeEndDate,
  } = useDateRange({
    defaultStartDate: useFromUnixTime(contractProduct?.startDate) || defaultStartDate,
    defaultEndDate: useFromUnixTime(contractProduct?.endDate) || defaultEndDate,
  });
  const [isToggled, setIsToggled] = useState(contractProduct ? contractProduct.enabled : true);
  const isMobile = useIsMobile();
  const deleteProduct = () => removeProductLineMethod(index);
  const switcherMethod = (booleanState: boolean) => setIsToggled(!booleanState);

  const { options: productOptions, isLoading: productsIsLoading } = useProductsOptions();

  if (productsIsLoading) {
    return <Spinner />;
  }

  return (
    <Row key={productKey} className="pb-2 align-items-center">
      <Form.Control defaultValue={contractProduct?.id} name="product_instance_id[]" type="hidden" />
      <Form.Control
        className="d-none"
        value={selectedProduct}
        name="product_id[]"
        required
      />
      <Col xs={4} className="pe-1">
        <Form.Group controlId="product-ids">
          <Selector
            id={fieldId}
            value={selectedProduct}
            setValue={setSelectedProduct}
            error="Um produto deve ser selecionado"
            validated
            isValid={selectedProduct !== undefined}
          >
            {productOptions?.map((productOption) => (
              <option key={productOption.value} value={productOption.value}>
                {productOption.label}
              </option>
            ))}
          </Selector>
        </Form.Group>
      </Col>
      <Col className="px-1">
        <DatePicker
          required
          selected={startDate}
          name="product_start_date[]"
          className="form-control"
          wrapperClassName="w-100"
          onChange={onChangeStartDate}
          selectsStart
          startDate={startDate || undefined}
          endDate={endDate || undefined}
          maxDate={endDate || undefined}
        />
      </Col>
      <Col className="px-1">
        <DatePicker
          required
          selected={endDate}
          name="product_end_date[]"
          className="form-control"
          wrapperClassName="w-100"
          onChange={onChangeEndDate}
          selectsEnd
          startDate={startDate || undefined}
          endDate={endDate || undefined}
          minDate={startDate || undefined}
        />
      </Col>
      <Col xs={2} className="d-flex justify-content-around align-items-center ps-1">
        <input type="hidden" name="product_enabled[]" value={`${isToggled}`} />
        <TooltipWrapper title={isToggled ? 'Desabilitar produto' : 'Habilitar produto'}>
          <Switch
            className={isMobile ? 'ds-switch-pill' : ''}
            checked={isToggled}
            onClick={() => switcherMethod(isToggled)}
          />
        </TooltipWrapper>
        {!contractProduct?.id && (
          <ActionButton
            icon="close"
            tooltipText="Remover produto"
            onClick={deleteProduct}
          />
        )}
      </Col>
    </Row>
  );
}
